import React from 'react';
import { useLingui } from '@lingui/react/macro';

import CarouselLeft from '@rover/icons/svg/carousel-left.svg';
import CarouselRight from '@rover/icons/svg/carousel-right.svg';
import { Button, Flex } from '@rover/kibble/core';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import { useScrollbarTrackWidth } from './hooks';

type LightboxPagerControlsProps = {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
};

/**
 * The tap controls shared by both pagers. Native pages by swipe, which WCAG
 * 2.5.1 requires a non-path-based alternative to; web has no swipe and needs
 * them outright. Each pager owns what a page change means and passes it in.
 */
const LightboxPagerControls = ({
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: LightboxPagerControlsProps): JSX.Element => {
  const { t } = useLingui();
  // `fixed` holds the controls against the viewport as the page scrolls. React
  // Native has no such position, so there they anchor to the pager itself.
  const position = isReactNative() ? 'absolute' : 'fixed';
  const scrollbarWidth = useScrollbarTrackWidth();

  return (
    <>
      <Flex
        testID="lightbox-prev-control"
        position={position}
        top="50%"
        left="4x"
        marginTop="-5x"
        zIndex="1"
      >
        <Button
          circular
          icon={CarouselLeft}
          aria-label={t`Previous`}
          disabled={!hasPrev}
          onPress={onPrev}
          testID="lightbox-prev-button"
        />
      </Flex>
      <Flex
        testID="lightbox-next-control"
        position={position}
        top="50%"
        right="4x"
        // The overlay reserves its scrollbar at the same viewport edge this control
        // is offset from, so shift past the track to keep the gap inside the content.
        webProps={{ sx: { marginRight: `${scrollbarWidth}px` } }}
        marginTop="-5x"
        zIndex="1"
      >
        <Button
          circular
          icon={CarouselRight}
          aria-label={t`Next`}
          disabled={!hasNext}
          onPress={onNext}
          testID="lightbox-next-button"
        />
      </Flex>
    </>
  );
};

export default LightboxPagerControls;
